import React, { useEffect, useState } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import {
  AssemblySummary,
  DisplayType,
  GetAssembliesSummaryQuery,
  TemplateTypeEnum,
  useGetAssembliesSummaryQuery,
} from '@/__generated__/graphql';
import * as analysisState from '@/components/Analysis/state';
import { useAtom, useAtomValue } from 'jotai';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  StackedBarChart,
  CircularBarChart,
  DynamicCounter,
  DeckListType,
} from './DisplayTypeComponents';

const displayTypeComponentMapping: Record<string, (item: AssemblySummary) => JSX.Element> = {
  [DisplayType.StackedBar]: (item) => (
    <StackedBarChart data={item.data || []} displayConfig={item.displayConfig || []} />
  ),
  [DisplayType.DeckImages]: () => <DeckListType />,
  [DisplayType.Counter]: (item) => (
    <DynamicCounter
      name={item.name}
      operation={item.operation}
      data={item.data || []}
      displayConfig={item.displayConfig || []}
    />
  ),
  [DisplayType.CircularBar]: (item) => (
    <CircularBarChart data={item.data || []} displayConfig={item.displayConfig || []} />
  ),
};

type GroupedByRowOrder = {
  [key: number]: AssemblySummary[];
};

export const QuerySummary = () => {
  const structureId = useGetStructureId();
  const templateId = useAtomValue(analysisState.selectedAssemblyTemplateId)!;

  const selectedTemplateFilters = useAtomValue(analysisState.selectedTemplateFiltersState);
  const [applyFilters, setApplyFilters] = useAtom(analysisState.applyFilters);
  const selectedFilteredDeckIds = useAtomValue(analysisState.selectedFilteredDeckIds);
  const selectedWorkpackIds = useAtomValue(analysisState.selectedFilteredWorkpackIds);
  const selectedPaintRegionIds = useAtomValue(analysisState.selectedFilteredPaintRegionIds);
  const [appliedTemplateFilters, setAppliedTemplateFilters] = useState(selectedTemplateFilters);
  const [appliedDeckFilters, setAppliedDeckFilters] = useState(selectedFilteredDeckIds);
  const [appliedWorkpackFilters, setAppliedWorkpackFilters] = useState(selectedWorkpackIds);

  const [appliedPaintRegionFilters, setAppliedPaintRegionFilters] =
    useState(selectedPaintRegionIds);
  const [assembliesSummary, setAssembliesSummary] = useState<
    GetAssembliesSummaryQuery['getAssembliesSummary']
  >([]);

  const { loading: summaryLoading } = useGetAssembliesSummaryQuery({
    variables: {
      input: {
        selectedTemplateFilters: appliedTemplateFilters,
        structureId,
        templateId,
        type: TemplateTypeEnum.Assembly,
        selectedDeckIds: appliedDeckFilters,
        selectedPaintRegionIds: appliedPaintRegionFilters,
        selectedWorkpackIds: appliedWorkpackFilters,
      },
    },
    fetchPolicy: 'network-only',
    onCompleted: (summaryData) => {
      if (summaryData?.getAssembliesSummary) {
        setAssembliesSummary(summaryData?.getAssembliesSummary);
        setApplyFilters(false);
      }
    },
    skip: !templateId,
  });

  useEffect(() => {
    if (applyFilters) {
      setAppliedTemplateFilters(selectedTemplateFilters);
      setAppliedDeckFilters(selectedFilteredDeckIds);
      setAppliedPaintRegionFilters(selectedPaintRegionIds);
      setAppliedWorkpackFilters(selectedWorkpackIds);
    }
  }, [
    applyFilters,
    selectedFilteredDeckIds,
    selectedPaintRegionIds,
    selectedTemplateFilters,
    selectedWorkpackIds,
  ]);

  if (summaryLoading) return <Skeleton variant="rectangular" width="100%" height={60} />;

  const groupedByRowOrder = assembliesSummary.reduce(
    (accumulator: GroupedByRowOrder, item: AssemblySummary) => {
      if (item.rowOrder && !accumulator[item.rowOrder]) {
        accumulator[item.rowOrder] = [];
      }
      accumulator[item.rowOrder].push(item);
      return accumulator;
    },
    {} as GroupedByRowOrder
  );

  return (
    <Box>
      {Object.entries(groupedByRowOrder).map(([rowOrder, items]) => (
        <Stack key={rowOrder} direction="row" alignItems="flex-start" sx={{ mb: 3, ml: 2 }}>
          {items.map((item) => {
            const RenderComponent = displayTypeComponentMapping[item.displayType];
            return (
              <Box key={item.id} style={{ flex: 1 }}>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, mb: 2 }}>
                  {item.name}
                </Typography>
                {RenderComponent(item)}
              </Box>
            );
          })}
        </Stack>
      ))}
    </Box>
  );
};
